import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "relevance", label: "Most relevant" },
  { value: "popular", label: "Most popular" },
  { value: "price-low", label: "Lowest price" },
  { value: "price-high", label: "Highest price" },
  { value: "newest", label: "Newest" },
];

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "ONE SIZE"];
const SUSTAINABILITY_OPTIONS = ["GOTS organic cotton", "GRS", "No certificate"];
const FIT_OPTIONS = ["Oversized", "Regular", "Relaxed", "Slim"];
const AVAILABILITY_OPTIONS = ["In stock", "Out of stock"];
const PRODUCTS = Array.isArray(productData) ? productData : [];
const FACET_PARAM_KEYS = [
  "category",
  "color",
  "size",
  "sustainability",
  "fit",
  "availability",
  "minPrice",
  "maxPrice",
];
const FILTER_PARAM_KEYS = [
  ...FACET_PARAM_KEYS,
  "tag",
  "collection",
];

const createEmptyFilters = () => ({
  categories: [],
  colors: [],
  sizes: [],
  sustainability: [],
  fits: [],
  availability: [],
  minPrice: "",
  maxPrice: "",
});

const normalize = (value) => String(value ?? "").trim().toLowerCase();

const uniqueValues = (values) =>
  [...new Set(values.filter(Boolean).map((value) => String(value).trim()))].sort(
    (a, b) => a.localeCompare(b)
  );

const toValues = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(toValues).filter(Boolean);
  }

  if (value === undefined || value === null || value === "") {
    return [];
  }

  if (typeof value === "object") {
    if (value.value !== undefined) return toValues(value.value);
    if (value.name !== undefined) return toValues(value.name);
    return Object.values(value).flatMap(toValues).filter(Boolean);
  }

  return String(value)
    .split(/[,|]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const readAttribute = (product, keys) => {
  const attributes = product?.attributes;
  if (!attributes) return [];

  const normalizedKeys = keys.map(normalize);

  if (Array.isArray(attributes)) {
    const match = attributes.find((attribute) =>
      normalizedKeys.includes(normalize(attribute?.name || attribute?.key || attribute?.label))
    );
    return match ? toValues(match.value ?? match.values) : [];
  }

  if (typeof attributes === "object") {
    const key = Object.keys(attributes).find((attributeKey) =>
      normalizedKeys.includes(normalize(attributeKey))
    );
    return key ? toValues(attributes[key]) : [];
  }

  return [];
};

const getValues = (product, keys) => {
  for (const key of keys) {
    if (product?.[key] !== undefined) {
      const values = toValues(product[key]);
      if (values.length) return values;
    }
  }
  return readAttribute(product, keys);
};

const getCategory = (product) =>
  getValues(product, ["category", "productType", "type"])[0] || "Essentials";

const getColors = (product) => getValues(product, ["colors", "color"]);
const getSizes = (product) => getValues(product, ["sizes", "size"]);
const getFit = (product) => getValues(product, ["fit", "silhouette"]);
const getTags = (product) => getValues(product, ["tags", "tag"]);

const getSustainability = (product) => {
  const explicit = getValues(product, ["sustainability", "certification", "certificate"]);
  if (explicit.length) return explicit;

  const tags = getTags(product);
  return tags.filter((tag) => /gots|grs|certificate|organic/i.test(tag));
};

const getPrice = (product) => {
  const value = product?.price?.amount ?? product?.price;
  const numeric = Number.parseFloat(String(value ?? "0").replace(",", "."));
  return Number.isFinite(numeric) ? numeric : 0;
};

const getCompareAtPrice = (product) => {
  const value =
    product?.compareAtPrice?.amount ??
    product?.compareAtPrice ??
    product?.originalPrice ??
    product?.oldPrice;
  const numeric = Number.parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(numeric) ? numeric : null;
};

const getImageSource = (image) => {
  if (typeof image === "string") return image;
  return image?.src || image?.url || image?.image || "";
};

const getImages = (product) => {
  const images = Array.isArray(product?.images)
    ? product.images.map(getImageSource).filter(Boolean)
    : [];
  const primary = getImageSource(product?.image);

  if (primary && !images.includes(primary)) images.unshift(primary);
  return images;
};

const isInStock = (product) => {
  if (product?.inStock === false || product?.available === false) return false;
  if (Number(product?.stock) === 0 || Number(product?.inventory) === 0) return false;

  const availability = getValues(product, ["availability", "stockStatus"])[0];
  return availability ? !/out|sold/i.test(availability) : true;
};

const getBadge = (product) => {
  if (product?.badge) return String(product.badge).toUpperCase();
  if (getCompareAtPrice(product) > getPrice(product)) return "SALE";
  if (product?.isNew || getTags(product).some((tag) => normalize(tag) === "new")) return "NEW";
  if (getTags(product).some((tag) => normalize(tag).includes("365"))) {
    return siteConfig.collections.coreLabel;
  }
  return "";
};

const parseParamList = (value) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const filtersFromParams = (params) => ({
  categories: parseParamList(params.get("category")),
  colors: parseParamList(params.get("color")),
  sizes: parseParamList(params.get("size")),
  sustainability: parseParamList(params.get("sustainability")),
  fits: parseParamList(params.get("fit")),
  availability: parseParamList(params.get("availability")),
  minPrice: params.get("minPrice") || "",
  maxPrice: params.get("maxPrice") || "",
});

const valuesOverlap = (productValues, selectedValues) => {
  if (!selectedValues.length) return true;
  const normalizedProductValues = productValues.map(normalize);

  return selectedValues.some((selected) => {
    const normalizedSelected = normalize(selected);
    return normalizedProductValues.some(
      (value) => value === normalizedSelected || value.includes(normalizedSelected)
    );
  });
};

const matchesProduct = (
  product,
  filters,
  query,
  tag,
  collection,
  applyTagFilter,
  applyCollectionFilter
) => {
  const searchableAttributes = product?.attributes
    ? toValues(product.attributes).join(" ")
    : "";
  const searchable = normalize(
    [
      product?.name,
      getCategory(product),
      getColors(product).join(" "),
      getTags(product).join(" "),
      Array.isArray(product?.description)
        ? product.description.join(" ")
        : product?.description,
      searchableAttributes,
    ].join(" ")
  );

  if (normalize(query) && !searchable.includes(normalize(query))) return false;
  if (!valuesOverlap([getCategory(product)], filters.categories)) return false;
  if (!valuesOverlap(getColors(product), filters.colors)) return false;
  if (!valuesOverlap(getSizes(product), filters.sizes)) return false;
  if (!valuesOverlap(getSustainability(product), filters.sustainability)) return false;
  if (!valuesOverlap(getFit(product), filters.fits)) return false;

  if (filters.availability.length) {
    const status = isInStock(product) ? "In stock" : "Out of stock";
    if (!filters.availability.some((value) => normalize(value) === normalize(status))) {
      return false;
    }
  }

  const price = getPrice(product);
  if (filters.minPrice !== "" && price < Number(filters.minPrice)) return false;
  if (filters.maxPrice !== "" && price > Number(filters.maxPrice)) return false;

  if (applyTagFilter && tag && !valuesOverlap(getTags(product), [tag])) return false;
  if (
    applyCollectionFilter &&
    collection &&
    !valuesOverlap(getTags(product), [collection]) &&
    !normalize(getCategory(product)).includes(normalize(collection))
  ) {
    return false;
  }

  return true;
};

const sortProducts = (products, sort, query) => {
  const sorted = [...products];

  switch (sort) {
    case "relevance":
      if (!normalize(query)) return sorted;
      return sorted.sort((a, b) => {
        const score = (product) => {
          const name = normalize(product.name);
          if (name === normalize(query)) return 3;
          if (name.startsWith(normalize(query))) return 2;
          if (name.includes(normalize(query))) return 1;
          return 0;
        };
        return score(b) - score(a);
      });
    case "popular":
      return sorted.sort(
        (a, b) =>
          Number(b.reviewCount || b.reviews || 0) -
          Number(a.reviewCount || a.reviews || 0)
      );
    case "price-low":
      return sorted.sort((a, b) => getPrice(a) - getPrice(b));
    case "price-high":
      return sorted.sort((a, b) => getPrice(b) - getPrice(a));
    case "newest":
      return sorted.sort((a, b) => {
        const aDate = Date.parse(a.createdAt || a.date || "") || Number(a.id) || 0;
        const bDate = Date.parse(b.createdAt || b.date || "") || Number(b.id) || 0;
        return bDate - aDate;
      });
    default:
      return sorted;
  }
};

function HeartIcon({ filled = false }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M20.8 4.9a5.5 5.5 0 0 0-7.8 0L12 6l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.3 1-1a5.5 5.5 0 0 0 0-7.8Z"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 6h18M6 12h12M9 18h6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m5 5 14 14M19 5 5 19" fill="none" stroke="currentColor" />
    </svg>
  );
}

function ProductMedia({ product, priority = false }) {
  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [secondaryError, setSecondaryError] = useState(false);
  const images = getImages(product);
  const primaryImage = images[0];
  const secondaryImage = images[1];

  return (
    <div className={`shop-product__media${loaded ? " is-loaded" : ""}`}>
      {!loaded && !hasError && <span className="shop-product__loader" aria-hidden="true" />}
      {primaryImage && !hasError ? (
        <img
          className="shop-product__image shop-product__image--primary"
          src={primaryImage}
          alt={product.name}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setHasError(true)}
        />
      ) : (
        <div className="shop-product__image-error" role="img" aria-label="Image unavailable">
          <span>IMAGE UNAVAILABLE</span>
        </div>
      )}
      {secondaryImage && !secondaryError && (
        <img
          className="shop-product__image shop-product__image--secondary"
          src={secondaryImage}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setSecondaryError(true)}
        />
      )}
    </div>
  );
}

function ProductCard({ product, priority, wished, onWishlist }) {
  const colors = getColors(product);
  const badge = getBadge(product);
  const price = getPrice(product);
  const compareAtPrice = getCompareAtPrice(product);
  const detailPath = `/shopDetails/${product.id}`;

  return (
    <article className="shop-product">
      <div className="shop-product__visual">
        <Link
          to={detailPath}
          className="shop-product__media-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label={`View ${product.name}`}
        >
          <ProductMedia product={product} priority={priority} />
        </Link>
        {badge && <span className="shop-product__badge">{badge}</span>}
        <button
          className={`shop-product__wishlist${wished ? " is-active" : ""}`}
          type="button"
          aria-label={`${wished ? "Remove" : "Add"} ${product.name} ${
            wished ? "from" : "to"
          } wishlist`}
          aria-pressed={wished}
          onClick={() => onWishlist(product.id)}
        >
          <HeartIcon filled={wished} />
        </button>
      </div>

      <div className="shop-product__info">
        <div className="shop-product__heading">
          <Link
            to={detailPath}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <h2>{product.name}</h2>
          </Link>
          <div className="shop-product__prices">
            <span className={compareAtPrice > price ? "is-sale" : ""}>
              {formatPrice(price)}
            </span>
            {compareAtPrice > price && (
              <del>{formatPrice(compareAtPrice)}</del>
            )}
          </div>
        </div>
        <div className="shop-product__meta">
          <span>{getColors(product)[0] || getCategory(product)}</span>
          {colors.length > 1 && <span>+ {colors.length - 1} colours</span>}
        </div>
      </div>
    </article>
  );
}

function CheckboxList({ name, options, selected, onChange }) {
  return (
    <div className={`shop-filter__options shop-filter__options--${name}`}>
      {options.map((option) => {
        const id = `shop-filter-${name}-${normalize(option).replace(/[^a-z0-9]+/g, "-")}`;
        return (
          <label htmlFor={id} key={option}>
            <input
              id={id}
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => onChange(name, option)}
            />
            <span aria-hidden="true" />
            {option}
          </label>
        );
      })}
    </div>
  );
}

function FilterSection({ title, children }) {
  return (
    <details className="shop-filter__section" open>
      <summary>{title}</summary>
      <div className="shop-filter__section-content">{children}</div>
    </details>
  );
}

function Shop() {
  const { wishlistItems = [], toggleWishlist } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsKey = searchParams.toString();
  const urlQuery = searchParams.get("q") || searchParams.get("search") || "";
  const sortValue = searchParams.get("sort") || "featured";
  const tag = searchParams.get("tag") || "";
  const collection = searchParams.get("collection") || "";
  const appliedFilters = useMemo(
    () => filtersFromParams(new URLSearchParams(paramsKey)),
    [paramsKey]
  );
  const [searchInput, setSearchInput] = useState(urlQuery);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [gridDensity, setGridDensity] = useState(2);
  const [draftFilters, setDraftFilters] = useState(appliedFilters);

  const products = PRODUCTS;
  const applyTagFilter = Boolean(tag);
  const applyCollectionFilter = Boolean(collection);

  useEffect(() => {
    setSearchInput(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    if (!drawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [drawerOpen]);

  const options = useMemo(() => {
    const categories = uniqueValues(products.map(getCategory));
    const colors = uniqueValues(products.flatMap(getColors));
    const productSizes = uniqueValues(products.flatMap(getSizes));
    const sizes = [...productSizes].sort((a, b) => {
      const aIndex = SIZE_ORDER.indexOf(a.toUpperCase());
      const bIndex = SIZE_ORDER.indexOf(b.toUpperCase());
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
    const sustainability = uniqueValues([
      ...SUSTAINABILITY_OPTIONS,
      ...products.flatMap(getSustainability),
    ]);
    const fits = uniqueValues([...FIT_OPTIONS, ...products.flatMap(getFit)]);
    const prices = products.map(getPrice).filter(Number.isFinite);

    return {
      categories,
      colors,
      sizes,
      sustainability,
      fits,
      minPrice: prices.length ? Math.floor(Math.min(...prices)) : 0,
      maxPrice: prices.length ? Math.ceil(Math.max(...prices)) : 500,
    };
  }, [products]);

  const matchedProducts = useMemo(
    () =>
      products.filter((product) =>
        matchesProduct(
          product,
          appliedFilters,
          searchInput,
          tag,
          collection,
          applyTagFilter,
          applyCollectionFilter
        )
      ),
    [
      products,
      appliedFilters,
      searchInput,
      tag,
      collection,
      applyTagFilter,
      applyCollectionFilter,
    ]
  );

  const visibleProducts = useMemo(
    () => sortProducts(matchedProducts, sortValue, searchInput),
    [matchedProducts, sortValue, searchInput]
  );

  const draftResultCount = useMemo(
    () =>
      products.filter((product) =>
        matchesProduct(
          product,
          draftFilters,
          searchInput,
          tag,
          collection,
          applyTagFilter,
          applyCollectionFilter
        )
      ).length,
    [
      products,
      draftFilters,
      searchInput,
      tag,
      collection,
      applyTagFilter,
      applyCollectionFilter,
    ]
  );

  const activeFilterCount = [
    ...appliedFilters.categories,
    ...appliedFilters.colors,
    ...appliedFilters.sizes,
    ...appliedFilters.sustainability,
    ...appliedFilters.fits,
    ...appliedFilters.availability,
    appliedFilters.minPrice,
    appliedFilters.maxPrice,
    tag,
    collection,
  ].filter(Boolean).length;

  const updateParams = (updates, keysToDelete = []) => {
    const next = new URLSearchParams(searchParams);
    keysToDelete.forEach((key) => next.delete(key));
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  };

  const openFilters = () => {
    setDraftFilters(filtersFromParams(searchParams));
    setDrawerOpen(true);
  };

  const toggleDraftFilter = (group, value) => {
    setDraftFilters((current) => {
      const isSelected = current[group].includes(value);
      return {
        ...current,
        [group]: isSelected
          ? current[group].filter((item) => item !== value)
          : [...current[group], value],
      };
    });
  };

  const showResults = () => {
    const next = new URLSearchParams(searchParams);
    FACET_PARAM_KEYS.forEach((key) => next.delete(key));
    const serialized = {
      category: draftFilters.categories.join(","),
      color: draftFilters.colors.join(","),
      size: draftFilters.sizes.join(","),
      sustainability: draftFilters.sustainability.join(","),
      fit: draftFilters.fits.join(","),
      availability: draftFilters.availability.join(","),
      minPrice: draftFilters.minPrice,
      maxPrice: draftFilters.maxPrice,
    };

    Object.entries(serialized).forEach(([key, value]) => {
      if (value) next.set(key, value);
    });
    setSearchParams(next, { replace: true });
    setDrawerOpen(false);
  };

  const clearAllApplied = () => {
    const next = new URLSearchParams(searchParams);
    [...FILTER_PARAM_KEYS, "q", "search"].forEach((key) => next.delete(key));
    setSearchInput("");
    setDraftFilters(createEmptyFilters());
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    updateParams({ q: searchInput.trim() }, ["search"]);
  };

  const handleWishlistToggle = (productId) => {
    if (typeof toggleWishlist === "function") toggleWishlist(productId);
  };

  const collectionKey = normalize(collection);
  const heroContent = {
    women: {
      eyebrow: "WOMEN'S COLLECTION",
      title: "EASE IN MOTION",
      image: "/img/editorial/shop-women-hero.jpg",
      heading: "ALL WOMEN",
    },
    men: {
      eyebrow: "MEN'S COLLECTION",
      title: "EASE IN MOTION",
      image: "/img/editorial/shop-men-hero.jpg",
      heading: "MEN",
    },
    homeware: {
      eyebrow: "HOMEWARE",
      title: "OBJECTS FOR EVERY DAY",
      image: "/img/editorial/shop-homeware-hero.jpg",
      heading: "HOMEWARE",
    },
    "365": {
      eyebrow: siteConfig.collections.coreLabel,
      title: "EVERYDAY ESSENTIALS",
      image: "/img/editorial/shop-core-hero.jpg",
      heading: siteConfig.collections.coreLabel,
    },
    new: {
      eyebrow: "NEW ARRIVALS",
      title: "FRESH PERSPECTIVE",
      image: "/img/editorial/shop-new-hero.jpg",
      heading: "NEW ARRIVALS",
    },
  }[collectionKey] || {
    eyebrow: "THE COLLECTION",
    title: "EASE IN MOTION",
    image: "/img/editorial/shop-all-hero.jpg",
    heading: "ALL ITEMS",
  };

  return (
    <div className="shop main">
      <main>
        <section className="shop-hero" aria-label={heroContent.eyebrow}>
          <img
            src={heroContent.image}
            alt={`${siteConfig.brandName} ${heroContent.eyebrow.toLowerCase()}`}
            loading="eager"
            decoding="async"
          />
          <div className="shop-hero__content">
            <span>{heroContent.eyebrow}</span>
            <h2>{heroContent.title}</h2>
          </div>
        </section>

        <section className="shop-toolbar" aria-label="Product controls">
          <div className="shop-toolbar__title">
            <h1>{heroContent.heading}</h1>
            <span aria-live="polite">{visibleProducts.length} items</span>
          </div>

          <div className="shop-toolbar__actions">
            <form className="shop-search" role="search" onSubmit={submitSearch}>
              <label htmlFor="shop-search-input">Search products</label>
              <input
                id="shop-search-input"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search"
                autoComplete="off"
              />
              <button type="submit">SEARCH</button>
            </form>

            <div className="shop-density" aria-label="Product grid density">
              <button
                type="button"
                className={gridDensity === 2 ? "is-active" : ""}
                onClick={() => setGridDensity(2)}
                aria-label="Show two products per row"
                aria-pressed={gridDensity === 2}
              >
                <i /><i />
              </button>
              <button
                type="button"
                className={gridDensity === 1 ? "is-active" : ""}
                onClick={() => setGridDensity(1)}
                aria-label="Show one product per row"
                aria-pressed={gridDensity === 1}
              >
                <i />
              </button>
            </div>

            <label className="shop-sort">
              <span>Sort by</span>
              <select
                value={SORT_OPTIONS.some((option) => option.value === sortValue) ? sortValue : "featured"}
                onChange={(event) => updateParams({ sort: event.target.value })}
              >
                {SORT_OPTIONS.map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <button className="shop-filter-trigger" type="button" onClick={openFilters}>
              <FilterIcon />
              FILTERS{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </button>
          </div>
        </section>

        {visibleProducts.length ? (
          <section
            className={`shop-grid${gridDensity === 1 ? " is-single" : ""}`}
            aria-label="Products"
          >
            {visibleProducts.map((product, index) => (
              <ProductCard
                product={product}
                priority={index < 4}
                wished={wishlistItems.some((item) =>
                  Number(item && typeof item === "object" ? item.id : item) ===
                  Number(product.id)
                )}
                onWishlist={handleWishlistToggle}
                key={product.id}
              />
            ))}
          </section>
        ) : (
          <section className="shop-empty" aria-live="polite">
            <p>NO PRODUCTS MATCH YOUR SELECTION.</p>
            <button type="button" onClick={clearAllApplied}>
              CLEAR FILTERS
            </button>
          </section>
        )}

        <button className="shop-filter-fab" type="button" onClick={openFilters}>
          <FilterIcon />
          <span>FILTER{activeFilterCount ? ` (${activeFilterCount})` : ""}</span>
        </button>
      </main>

      <button
        className={`shop-filter-backdrop${drawerOpen ? " is-open" : ""}`}
        type="button"
        aria-label="Close filters"
        tabIndex={drawerOpen ? 0 : -1}
        onClick={() => setDrawerOpen(false)}
      />
      <aside
        className={`shop-filter${drawerOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shop-filter-title"
        aria-hidden={!drawerOpen}
      >
        <header className="shop-filter__header">
          <h2 id="shop-filter-title">FILTERS</h2>
          <button type="button" aria-label="Close filters" onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </button>
        </header>

        <div className="shop-filter__body">
          <FilterSection title="Product type">
            <CheckboxList
              name="categories"
              options={options.categories}
              selected={draftFilters.categories}
              onChange={toggleDraftFilter}
            />
          </FilterSection>

          <FilterSection title="Price range">
            <div className="shop-filter__price">
              <label>
                <span>From</span>
                <div>
                  <i>$</i>
                  <input
                    type="number"
                    min={options.minPrice}
                    max={options.maxPrice}
                    value={draftFilters.minPrice}
                    placeholder={String(options.minPrice)}
                    onChange={(event) =>
                      setDraftFilters((current) => ({
                        ...current,
                        minPrice: event.target.value,
                      }))
                    }
                  />
                </div>
              </label>
              <label>
                <span>To</span>
                <div>
                  <i>$</i>
                  <input
                    type="number"
                    min={options.minPrice}
                    max={options.maxPrice}
                    value={draftFilters.maxPrice}
                    placeholder={String(options.maxPrice)}
                    onChange={(event) =>
                      setDraftFilters((current) => ({
                        ...current,
                        maxPrice: event.target.value,
                      }))
                    }
                  />
                </div>
              </label>
            </div>
          </FilterSection>

          <FilterSection title="Color">
            <CheckboxList
              name="colors"
              options={options.colors}
              selected={draftFilters.colors}
              onChange={toggleDraftFilter}
            />
          </FilterSection>

          <FilterSection title="Size">
            <CheckboxList
              name="sizes"
              options={options.sizes}
              selected={draftFilters.sizes}
              onChange={toggleDraftFilter}
            />
          </FilterSection>

          <FilterSection title="Sustainability">
            <CheckboxList
              name="sustainability"
              options={options.sustainability}
              selected={draftFilters.sustainability}
              onChange={toggleDraftFilter}
            />
          </FilterSection>

          <FilterSection title="Fit">
            <CheckboxList
              name="fits"
              options={options.fits}
              selected={draftFilters.fits}
              onChange={toggleDraftFilter}
            />
          </FilterSection>

          <FilterSection title="Availability">
            <CheckboxList
              name="availability"
              options={AVAILABILITY_OPTIONS}
              selected={draftFilters.availability}
              onChange={toggleDraftFilter}
            />
          </FilterSection>
        </div>

        <footer className="shop-filter__footer">
          <button type="button" onClick={() => setDraftFilters(createEmptyFilters())}>
            CLEAR ALL
          </button>
          <button type="button" onClick={showResults}>
            SHOW {draftResultCount} RESULTS
          </button>
        </footer>
      </aside>
    </div>
  );
}

export default Shop;
